import React, {useState, useRef} from 'react';
import {Map, List} from 'immutable';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  addToast,
  Anchor,
  Button,
  Heading,
  IconButton,
  SelectField,
  useTooltipWithLiveRegion,
  Text,
  TextField
} from '@albert-io/atomic';
import errorCodes from 'client/errorCodes';
import {getModelForResourceType} from 'resources/modelRegistry';
import {SALUTATIONS} from 'resources/augmented/User/user.extensions';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import SchoolSearchModal from 'components/SchoolSearchModal/SchoolSearchModal.react';

import './account-profile.scss';

const renderErrors = (errors) => {
  return errors ? errors.join(' ') : '';
};

const AccountProfile = ({user, setIsReady}) => {
  const [userUnderEdit, setUserUnderEdit] = useState(user);
  const [schoolUnderEdit, setSchoolUnderEdit] = useState(user.getSchool());

  const [isSavePending, setIsSavePending] = useState(false);
  const [errorMessages, setErrorMessages] = useState(List([]));
  const [showEditSchoolModal, setShowEditSchoolModal] = useState(false);

  const origUserRef = useRef(user);
  const formHasChanges = origUserRef.current !== userUnderEdit;

  const isTeacher = user.isTeacher();
  const isTeacherAfterSave = userUnderEdit.isTeacher();

  const canChangeIsTeacher = !(
    (isTeacher && user.getTeacher().getClassrooms().size > 0) ||
    (!isTeacher && user.getStudent().getClassrooms().size > 0)
  );

  const lastNameOrSchoolIdPlaceholder = isTeacherAfterSave
    ? 'Last name'
    : 'Last name (or school ID)';

  // school.getAddress() only available via school from user
  // fallback cases after "||"" come from school selected via modal
  const schoolAddress = schoolUnderEdit?.getFullAddress?.().split(', ');
  const schoolStreetAddress = schoolAddress?.[0] || schoolUnderEdit?.getAddressLine1?.();
  const schoolCity = schoolAddress?.[1] || schoolUnderEdit?.getCity?.();
  const schoolState = schoolAddress?.[2] || schoolUnderEdit?.getState?.();
  const schoolPostalCode = schoolAddress?.[3] || schoolUnderEdit?.getPostalCode?.();

  const accountTypeTooltipAnchorRef = useRef(null);
  const {
    closeTooltip: closeAccountTypeTooltip,
    handleEscape: handleAccountTypeEscape,
    openTooltip: openAccountTypeTooltip,
    tooltipContentRef: accountTypeTooltipContentRef,
    tooltipProps: accountTypeTooltipProps,
    TooltipWithLiveRegion: AccountTypeTooltipWithLiveRegion
  } = useTooltipWithLiveRegion(accountTypeTooltipAnchorRef);

  const emailTooltipAnchorRef = useRef(null);
  const {
    closeTooltip: closeEmailTooltip,
    handleEscape: handleEmailEscape,
    openTooltip: openEmailTooltip,
    tooltipContentRef: emailTooltipContentRef,
    tooltipProps: emailTooltipProps,
    TooltipWithLiveRegion: EmailTooltipWithLiveRegion
  } = useTooltipWithLiveRegion(emailTooltipAnchorRef);

  const updateField = ({setter, value}) => {
    const updatedUser = userUnderEdit[setter](value);
    setUserUnderEdit(updatedUser);
  };

  const updateUserSchool = async (newSchool) => {
    try {
      // we update the passed in user because userUnderEdit might have
      // other changes (name, username) that we don't want to save along with the school
      const updatedUser = user.replaceRelationship({
        type: 'school_v2',
        relation: newSchool.getId()
      });
      await updatedUser.save();
      invalidatePartialInterest({resourcePath: ['teachers_v1', updatedUser.getId()]});

      // we still want to update the userUnderEdit so if there are other
      // changes that are saved, they don't save with the "old" school
      const updatedUserUnderEdit = userUnderEdit.replaceRelationship({
        type: 'school_v2',
        relation: newSchool.getId()
      });

      setSchoolUnderEdit(newSchool);
      setUserUnderEdit(updatedUserUnderEdit);
      // if there are school_id error messages (i.e. no school_id assigned),
      // then filter them out when we change the school_id value.
      if (errorMessages.some((item) => item.get('field') === 'school_id')) {
        setErrorMessages(errorMessages.filter((item) => item.get('field') !== 'school_id'));
      }

      setIsReady(false);
      addToast({
        message: 'Your school assocation has been updated successfully.',
        title: 'Success',
        type: 'success'
      });
    } catch (e) {
      addToast({
        message: 'There was a problem updating your school.',
        title: 'Error',
        type: 'danger'
      });
    }
  };

  const saveUserDetails = async () => {
    if (userUnderEdit.validators.hasValidationErrors()) {
      return;
    }
    setIsSavePending(true);

    // prevent teachers who aren't associated with a school from saving
    if (userUnderEdit.isTeacher() && schoolUnderEdit.size === 0) {
      setErrorMessages(
        List([
          Map({
            field: 'school_id',
            message: 'Your account must be associated with a school.'
          })
        ])
      );

      addToast({
        message: 'There was a problem updating your user details.',
        title: 'Error',
        type: 'danger'
      });
      setIsSavePending(false);
      return;
    }

    try {
      await userUnderEdit.save();
      setIsReady(false);
      addToast({
        message: 'Your user details have been updated successfully.',
        title: 'Success',
        type: 'success'
      });
      setIsSavePending(false);
    } catch (e) {
      logger.error('Error updating user details: %s', e.message);
      addToast({
        message: 'There was a problem updating your user details.',
        title: 'Error',
        type: 'danger'
      });

      if (e.status === 400) {
        const {errors} = e.response.body;
        const userNameError = errors.find((error) => {
          return (
            error.source.parameter === 'username' &&
            error.code === errorCodes.BAD_REQUEST.VALIDATION_ERROR.UNIQUE_CONSTRAINT_VIOLATED
          );
        });

        const schoolIdError = errors.find((error) => {
          return error.source.parameter === 'school_id';
        });

        if (schoolIdError) {
          setErrorMessages(
            List([
              Map({
                field: 'school_id',
                message: 'Your account must be associated with a school.'
              })
            ])
          );
        }

        if (userNameError) {
          setErrorMessages(
            List([
              Map({
                field: 'username',
                message: 'That username is already in use.'
              })
            ])
          );
        }
      }
      setIsSavePending(false);
    }
  };

  return (
    <>
      <Heading size='xxs' className='account-profile__header u-mar-b_4'>
        Profile
      </Heading>
      <form className='account-profile u-pad_4'>
        {!errorMessages.isEmpty() ? (
          <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
            <div className='account-profile__form-error-container'>
              <ul>
                {errorMessages.map((error, i) => {
                  return (
                    <li key={i}>
                      <span className='u-font-weight_bold u-mar-r_1'>Error:</span>
                      {error.get('message')}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : null}
        <div className='u-display_flex u-flex-wrap_no-wrap u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
          <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0'>
            <SelectField
              border
              contentEditable={canChangeIsTeacher}
              readOnly={!canChangeIsTeacher}
              label='Account type'
              name='isTeacher'
              value={userUnderEdit.isTeacher() ? 'Teacher' : 'Student'}
              options={[{value: 'Teacher'}, {value: 'Student'}]}
              onChange={(e) => {
                updateField({
                  setter: 'setIsTeacher',
                  value: e.target.value === 'Teacher'
                });
              }}
            />
          </div>
          <div
            className='account-profile__form-col u-display_flex u-mar-r_1 u-mar-tb_0 u-mar-l_0 u-align-items_center'
            id='account-profile__accounttype-tooltip'
          >
            <IconButton
              icon='info-circle'
              iconStyle='regular'
              onBlur={closeAccountTypeTooltip}
              onClick={openAccountTypeTooltip}
              onKeyDown={handleAccountTypeEscape}
              ref={accountTypeTooltipAnchorRef}
            />
            <AccountTypeTooltipWithLiveRegion
              {...accountTypeTooltipProps}
              placement='right'
              theme='light'
            >
              <span ref={accountTypeTooltipContentRef}>
                If you need to change your account type, please contact{' '}
                <Anchor
                  inheritColor
                  underlined
                  href='mailto:hello@albert.io?subject=Change%account%type'
                >
                  hello@albert.io
                </Anchor>
              </span>
            </AccountTypeTooltipWithLiveRegion>
          </div>
        </div>
        {isTeacherAfterSave ? (
          <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
            <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0'>
              <SelectField
                contentEditable
                border
                label='Title'
                name='salutation'
                value={userUnderEdit.getSalutation()}
                error={userUnderEdit.validators.getSalutation() !== null}
                message={renderErrors(userUnderEdit.validators.getSalutation())}
                onChange={(e) => {
                  updateField({
                    setter: 'setSalutation',
                    value: e.target.value
                  });
                }}
                options={SALUTATIONS.map((item) => ({value: item}))}
              />
            </div>
            <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0' />
          </div>
        ) : null}
        <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
          <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0'>
            <TextField
              border
              label='First name'
              maxLength={50}
              name='firstName'
              value={userUnderEdit.getFirstName()}
              error={userUnderEdit.validators.getFirstName() !== null}
              message={renderErrors(userUnderEdit.validators.getFirstName())}
              onChange={(e) => {
                updateField({
                  setter: 'setFirstName',
                  value: e.target.value
                });
              }}
            />
          </div>
          <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0'>
            <TextField
              border
              label={lastNameOrSchoolIdPlaceholder}
              maxLength={50}
              name='lastName'
              className='account-profile__field-padding'
              value={userUnderEdit.getLastName()}
              error={userUnderEdit.validators.getLastName() !== null}
              message={renderErrors(userUnderEdit.validators.getLastName())}
              onChange={(e) => {
                updateField({
                  setter: 'setLastName',
                  value: e.target.value
                });
              }}
            />
          </div>
        </div>
        <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
          <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0'>
            <TextField
              border
              label='Username'
              maxLength={80}
              name='username'
              value={userUnderEdit.getUsername()}
              error={
                userUnderEdit.validators.getUsername() !== null ||
                errorMessages.some((item) => item.get('field') === 'username')
              }
              message={renderErrors(userUnderEdit.validators.getUsername())}
              onChange={(e) => {
                // if there are username error messages (i.e. username already taken),
                // then filter them out when we change the username value.
                if (errorMessages.some((item) => item.get('field') === 'username')) {
                  setErrorMessages(
                    errorMessages.filter((item) => item.get('field') !== 'username')
                  );
                }

                updateField({
                  setter: 'setUsername',
                  value: e.target.value
                });
              }}
            />
          </div>
          <div className='account-profile__form-col u-display_flex u-mar-r_2 u-mar-tb_0 u-mar-l_0 u-align-items_center'>
            <TextField
              border
              readOnly
              label='Email'
              maxLength={250}
              name='email'
              className='u-mar-r_2'
              value={userUnderEdit.getEmail()}
              error={userUnderEdit.validators.getEmail() !== null}
              message={renderErrors(userUnderEdit.validators.getEmail())}
              onChange={(e) => {
                updateField({
                  setter: 'setEmail',
                  value: e.target.value
                });
              }}
            />
            <IconButton
              icon='info-circle'
              iconStyle='regular'
              color='secondary'
              transparent
              className='u-mar-t_4'
              onBlur={closeEmailTooltip}
              onClick={openEmailTooltip}
              onKeyDown={handleEmailEscape}
              ref={emailTooltipAnchorRef}
            />
            <EmailTooltipWithLiveRegion {...emailTooltipProps} placement='right' theme='light'>
              <span ref={emailTooltipContentRef}>
                Change your email by sending your current email, username and desired email to{' '}
                <Anchor
                  inheritColor
                  underlined
                  href='mailto:hello@albert.io?subject=Change%account%20email'
                >
                  hello@albert.io
                </Anchor>
                .
              </span>
            </EmailTooltipWithLiveRegion>
          </div>
        </div>
        <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
          <div className='account-profile__form-col u-display_flex u-mar-r_1 u-mar-tb_0 u-mar-l_0'>
            <div className='u-display_flex u-flex-direction_column u-flex-grow_1'>
              <div className='u-font-weight_bold u-mar-b_1 u-mar-lr_0 u-mar-t_0'>
                <Text emphasis='low' color='black' size='m'>
                  School association
                </Text>
              </div>
              <div
                className={classnames(
                  'account-profile__school-association-col u-pad_2 u-display_flex u-flex-direction_column',
                  {
                    'school-association-error': errorMessages.some(
                      (item) => item.get('field') === 'school_id'
                    )
                  }
                )}
              >
                {/* check if a school exists, if so render information. else render "no school" */}
                {schoolUnderEdit?.getName?.() ? (
                  <>
                    <span className='u-mar-b_1 u-font-weight_bold'>
                      <Text emphasis='high' size='s'>
                        {schoolUnderEdit?.getName()}
                      </Text>
                    </span>
                    {schoolUnderEdit.getIn(['district', 'name'], '') ? (
                      <span className='u-mar-b_1 u-font-weight_bold'>
                        <Text emphasis='high' size='xs'>
                          {schoolUnderEdit.getIn(['district', 'name'], '')}
                        </Text>
                      </span>
                    ) : null}
                    <span className='u-mar-b_1'>
                      <Text emphasis='lowest' size='xs'>
                        {schoolStreetAddress}
                      </Text>
                    </span>
                    <span className='u-mar-b_1'>
                      <Text emphasis='lowest' size='xs'>
                        {schoolCity}, {schoolState} {schoolPostalCode}
                      </Text>
                    </span>
                  </>
                ) : (
                  <span className='u-mar-b_1 u-font-weight_bold'>
                    <Text emphasis='high' size='s'>
                      No school
                    </Text>
                  </span>
                )}
                <Anchor
                  className='account-profile__edit-button'
                  underlined
                  onClick={() => setShowEditSchoolModal(true)}
                >
                  Edit
                </Anchor>
              </div>
            </div>
          </div>
          <div className='account-profile__form-col u-display_flex u-mar-r_1 u-mar-tb_0 u-mar-l_0' />
          {showEditSchoolModal && (
            <SchoolSearchModal
              handleClose={() => setShowEditSchoolModal(false)}
              hasExistingRecord={(schoolId) => {
                return schoolUnderEdit.getId() === schoolId;
              }}
              updateUserSchool={updateUserSchool}
              isTeacher={isTeacherAfterSave}
            />
          )}
        </div>
        <div className='account-profile__form-row u-display_flex u-mar-t_0 u-mar-lr_0 u-mar-b_4'>
          <div className='account-profile__form-col u-display_flex u-mar-r_1 u-mar-tb_0 u-mar-l_0'>
            <Button
              type='button'
              disabled={isSavePending || !formHasChanges}
              onClick={() => saveUserDetails()}
            >
              Save changes
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

AccountProfile.propTypes = {
  user: PropTypes.instanceOf(getModelForResourceType('user_v2')).isRequired,
  setIsReady: PropTypes.func.isRequired
};

export default AccountProfile;
